import * as React from "react";

import { Button, Theme, TextField } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import { makeStyles } from "@material-ui/core/styles";
import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import { consts } from "../../../common/consts";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import Grid from "@material-ui/core/Grid";
import { useHistory } from "react-router-dom";

import { ReactComponent as SMALogo } from "../../../assets/svgs/smalogo.svg";
import { signIn } from "../loginHelpers";
import { useDispatch } from "react-redux";

interface LoginWidgetForTabletsProps {
  finishLogin(isSuccess: boolean, token: string): void;
  goToForgotPassword(): void;
  goToLoginOTP(username: string, password: string, isSMS: boolean): void;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: "auto",
  },
  inputs: {
    fontSize: 14,

    "@media (max-width: 600px)": {
      textAlign: "left",
    },    
  },
  buttonContainer: {
    textAlign: "right",
  },
  button: {
    minWidth: 138,
    backgroundColor: "#FFB500",
    color: "#013270",
    borderRadius: 30,
    "&:hover": {
      color: "#0049a3",
      backgroundColor: "#013270",
    },
  },
  link: {
    color: "#F5F5F5",
    paddingLeft: 0,
    fontSize: "small",
  },
  alertErrorRoot: {
    marginBottom: 20,
  },
  textFieldRoot: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      height: 50,
      "&:hover fieldset": {
        borderColor: "rgba(255,255,255, 0.25)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "rgba(255,255,255, 0.25)",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 10px",
    },
    borderRadius: "4px",
  },

  textFieldInput: {
    backgroundColor: "#ffffff",
    color: "#666666",
    fontSize: 16,
  },
  logo: {
    maxWidth: 180,
    width: "90%",
    maxHeight: 162,
    cursor: "pointer",
  },
  contentContainer: {
    margin: "auto",
    backgroundColor: "#001630",
    backgroundImage: "url(https://smalive.wisdom.com.au/app/uploads/2021/06/bg-overlay-03-1.jpg)",
    backgroundPosition: "center center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    height: "100%",
    width: "100%",
  },

  headerBody: {
    display: "flex",
    padding: "0 25px",
  },
  widgetContent: {
    padding: 40,
    textAlign: "center",
  },
  headerLogin: {
    marginTop: 30,
    paddingBottom: 0,
  },
  headerLogo: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
  },
  heading: {
    fontSize: "100px",
    color: "#FFFFFF",
    fontWeight: 800,
    lineHeight: "1.2em",
    margin: 0,
  },
  loginText: {
    color: "#ffffff",
    marginBottom: 0,
    fontSize: 36,
  },
}));

function NewlineText(props: any) {
  const text = props.text;
  return <div style={{ whiteSpace: "pre-wrap" }}>{text}</div>;
}

const LoginWidgetForTablets: React.FC<LoginWidgetForTabletsProps> = (props) => {
  const { handleSubmit, errors, control, formState, trigger, watch } = useForm();
  const history = useHistory();
  const dispatch = useDispatch();

  const classes = useStyles();
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const LoginPage = () => {
    history.push("/login");
  };
  const handleSignIn = async (data: any) => {
    await signIn(data, dispatch, props.finishLogin, props.goToLoginOTP, setErrorMessage);
  };
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const emailValue = watch("email", "");
  React.useEffect(() => {
    if (/[0-9]+/.test(emailValue)) {
      trigger();
    }
  }, [emailValue]);

  return (
    <div className={classes.contentContainer}>
      <Grid className={classes.headerBody} container>
        <Grid item xs={12} sm={4} className={classes.headerLogo}>
          <SMALogo className={classes.logo} onClick={LoginPage} />
        </Grid>
        <Grid item xs={12} sm={8} className={classes.headerLogin}>
            <form onSubmit={handleSubmit(handleSignIn)}>
              <Grid spacing={3} container>
                <Grid item xs={12} className={classes.loginText}>
                  Sign In
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    defaultValue=""
                    control={control}
                    as={<TextField classes={{ root: classes.textFieldRoot }} InputProps={{ classes: { root: classes.textFieldInput } }} variant="outlined" placeholder="Email Address / Employee Id" error={errors.email} helperText={errors.email ? errors.email.message : ""} />}
                    name="email"
                    rules={{
                      required: "Email Address / Employee Id is required",
                      pattern: {
                        value: consts.EmailEmployeeIDRegex,
                        message: "Invalid email address / employee id",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    defaultValue=""
                    control={control}
                    as={
                      <TextField
                        classes={{ root: classes.textFieldRoot }}
                        InputProps={{
                          classes: { root: classes.textFieldInput },
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton style={{ color: "lightgrey" }} aria-label="toggle password visibility" onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword}>
                                {showPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        variant="outlined"
                        placeholder="Password"
                        type={showPassword ? "text" : "password"}
                        error={errors.password}
                        helperText={errors.password ? errors.password.message : ""}
                      />
                    }
                    name="password"
                    rules={{
                      validate: (value) => {
                        if (/[0-9]+/.test(emailValue)) return true;
                        else if (value === "") return "Password is required";
                        return true;
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button onClick={() => props.goToForgotPassword()} className={classes.link}>
                    Forgot your password?
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} className={classes.buttonContainer}>
                  <div className={classes.inputs}>
                    <Button type="submit" disabled={formState.isSubmitting} className={classes.button} variant="contained">
                      Sign In
                    </Button>
                  </div>
                </Grid>
                <Alert variant="filled" icon={<ReportProblemOutlinedIcon />} severity="error" classes={{ root: classes.alertErrorRoot }} style={{ display: errorMessage ? "flex" : "none" }}>
                  <NewlineText text={errorMessage} />
                </Alert>
              </Grid>
            </form>
        </Grid>
      </Grid>
    </div>
  );
};

export default React.memo(LoginWidgetForTablets);
